<template>
  <section id="pricing-plan">
    <FreeAccessCelebration
      v-if="celebrationVisible && freeAccessActive && accountUserId"
      :free-access="freeAccess"
      :user-id="accountUserId"
      :approval="account && account.approval"
      @closed="celebrationVisible = false"
    />
    <div
      v-if="planSelected && selectedOffer"
      class="checkout-address"
    >
      <CheckoutAddress
        :free-access="freeAccess"
        :total="priceNcount"
        :plan-key="selectedOffer.planKey"
        :selected-offer="selectedOffer"
        :review-starts-on-subscription="selectedReviewStartsOnSubscription"
        @offer-changed="handleOfferChanged"
      />
    </div>

    <template v-else>
      <b-card
        v-if="loadState === 'loading'"
        class="text-center"
        aria-live="polite"
      >
        {{ $t('Abonelik seçenekleri yükleniyor...') }}
      </b-card>

      <b-alert
        v-else-if="loadState === 'error'"
        variant="danger"
        show
        class="p-2"
      >
        <p class="mb-1">
          {{ $t('Abonelik seçenekleri şu anda yüklenemedi. Lütfen yeniden deneyin.') }}
        </p>
        <b-button
          variant="outline-danger"
          size="sm"
          @click="loadSubscriptionStatus"
        >
          {{ $t('Yeniden Dene') }}
        </b-button>
      </b-alert>

      <b-card
        v-else-if="accountBlocked"
        class="text-center"
      >
        <b-card-title>{{ $t('Hesabınız İçin Destek Gerekiyor') }}</b-card-title>
        <b-card-text>
          {{ $t('Hesabınızla ilgili bu işlemi tamamlamak için destek ekibimizle iletişime geçin.') }}
        </b-card-text>
      </b-card>

      <template v-else-if="applicationRejected">
        <b-card class="text-center">
          <b-card-title>{{ $t('Bilgilerinizde Düzenleme Gerekiyor') }}</b-card-title>
          <b-card-text>
            <p>{{ $t('Aboneliğe devam etmek için inceleme notunu görüntüleyip bilgilerinizi güncelleyin.') }}</p>
            <p
              v-if="reviewComment"
              class="review-note"
            >
              <strong>{{ $t('İnceleme Notu') }}:</strong> {{ reviewComment }}
            </p>
            <p v-if="reviewState === 'active' && reviewDeadlineAt">
              {{ $t('approvalLifecycle.rejected.activeCorrection', { date: formatDate(reviewDeadlineAt) }) }}
            </p>
            <p v-else-if="reviewState === 'completed'">
              {{ $t('approvalLifecycle.rejected.completed') }}
            </p>
            <p v-if="promotionRedeemed">
              {{ $t('Promosyon paketiniz daha önce kullanıldığı için yeniden sunulamaz.') }}
            </p>
          </b-card-text>
          <b-button
            variant="primary"
            :to="{ name: 'account-settings' }"
          >
            {{ $t('Bilgilerimi Güncelle') }}
          </b-button>
        </b-card>
        <template v-if="hasCurrentSubscription">
          <SubscriptionSummaryCard
            class="mt-2"
            v-bind="summaryCardProps"
          />
          <div class="text-center mt-1">
            <b-button
              variant="flat-primary"
              size="sm"
              :disabled="cancellationFlowInFlight"
              @click="cancelSubscription"
            >
              {{ $t('Cancel Subscription') }}
            </b-button>
          </div>
        </template>
        <CancellationResultCard
          v-if="isBlocked"
          class="mt-2"
          :access-end-date="retainedAccessEndDate"
          :title="cancellationResultTitle"
          :message="retainedAccessMessage"
          :unavailable-message="cancellationDateUnavailableMessage"
        />
      </template>

      <template v-else-if="reviewCompleted">
        <b-card class="text-center">
          <b-card-title>{{ $t('İnceleme Süresi Tamamlandı') }}</b-card-title>
          <b-card-text>
            {{ $t('İnceleme sonucu henüz hesabınıza yansımadı. Durum güncellendiğinde size bildirilecektir.') }}
          </b-card-text>
          <b-button
            variant="outline-primary"
            :to="{ name: 'account-settings' }"
          >
            {{ $t('Başvuru Durumumu Gör') }}
          </b-button>
        </b-card>
        <template v-if="hasCurrentSubscription">
          <SubscriptionSummaryCard
            class="mt-2"
            v-bind="summaryCardProps"
          />
          <div class="text-center mt-1">
            <b-button
              variant="flat-primary"
              size="sm"
              :disabled="cancellationFlowInFlight"
              @click="cancelSubscription"
            >
              {{ $t('Cancel Subscription') }}
            </b-button>
          </div>
        </template>
        <CancellationResultCard
          v-if="isBlocked"
          class="mt-2"
          :access-end-date="retainedAccessEndDate"
          :title="cancellationResultTitle"
          :message="retainedAccessMessage"
          :unavailable-message="cancellationDateUnavailableMessage"
        />
      </template>

      <template v-else>
        <b-card
          v-if="applicationNotSubmitted"
          class="text-center mb-2"
        >
          <b-card-text>
            {{ $t(freeAccess.eligible ? 'freeAccess.notSubmittedOffer' : 'pricing.accountCompletionRequired') }}
          </b-card-text>
          <b-button
            variant="primary"
            :to="{ name: 'account-settings' }"
          >
            {{ $t('approvalLifecycle.notSubmitted.completeCta') }}
          </b-button>
        </b-card>

        <b-row
          v-if="freeAccessClaimable || pendingAwaitingFreeAccess"
          class="pricing-card plan-options justify-content-center mt-2 mb-3"
        >
          <b-col
            cols="12"
            sm="10"
            md="8"
            lg="5"
          >
            <b-card
              class="price-card price-card--static text-left shadow-lg"
              body-class="plan-card-body"
              data-testid="free-access-offer-card"
            >
              <div class="text-center">
                <feather-icon
                  icon="GiftIcon"
                  size="32"
                  class="text-primary mb-1"
                />
                <h3 class="font-weight-bolder text-primary mb-1">
                  {{ $t(freeAccessClaimable ? 'freeAccess.availableTitle' : 'freeAccess.awaitingApprovalTitle') }}
                </h3>
                <p class="mb-0">
                  {{ $t(freeAccessClaimable ? 'freeAccess.availableBody' : 'freeAccess.startsOnApproval') }}
                </p>
              </div>
              <hr class="price-card__rule">
              <ul class="list-unstyled price-card__features">
                <li
                  v-for="feature in packageFeatures"
                  :key="feature"
                  class="price-card__feature d-flex align-items-center"
                >
                  <feather-icon
                    icon="CheckCircleIcon"
                    class="text-primary mr-1 flex-shrink-0"
                    size="20"
                  />
                  <span class="font-weight-bold font-medium-1">{{ $t(feature) }}</span>
                </li>
              </ul>
              <b-alert
                :show="Boolean(claimError)"
                variant="danger"
                class="p-1"
              >
                {{ $t(claimError || 'freeAccess.errors.notEligible') }}
              </b-alert>
              <b-button
                v-if="freeAccessClaimable"
                variant="primary"
                class="w-100 font-weight-bold"
                size="lg"
                :disabled="claiming"
                data-testid="claim-free-access"
                @click="claimFreeAccess"
              >
                {{ $t(claiming ? 'freeAccess.claiming' : 'freeAccess.claimCta') }}
              </b-button>
            </b-card>
          </b-col>
        </b-row>
        <b-alert
          v-if="freeAccessEnded"
          show
          variant="warning"
        >
          {{ $t('freeAccess.endedOn', { date: formatDate(freeAccess.endsAt) }) }}
        </b-alert>

        <b-card
          v-if="pendingPaidOfferUnavailable"
          class="text-center"
        >
          <b-card-title>{{ $t('approvalLifecycle.pending.title') }}</b-card-title>
          <b-card-text>
            {{ $t('approvalLifecycle.pending.paidPlanAfterApproval') }}
          </b-card-text>
          <b-button
            variant="outline-primary"
            :to="{ name: 'account-settings' }"
          >
            {{ $t('Başvuru Durumumu Gör') }}
          </b-button>
        </b-card>

        <b-row v-else-if="hasCurrentSubscription">
          <b-col
            sm="10"
            md="8"
            lg="6"
            class="mx-auto"
          >
            <SubscriptionSummaryCard
              v-bind="summaryCardProps"
            />
            <!-- Item 8: the cancellation action sits below the card and is
                 low-emphasis. It used to be a full `variant="danger"` block
                 inside the card, competing with it. PM 06.08.2026 item 2 took
                 the remaining red out too -- cancelling is a normal account
                 action, so it wears the brand colour, not the danger one. -->
            <div class="text-center mt-1">
              <b-button
                variant="flat-primary"
                size="sm"
                :disabled="cancellationFlowInFlight"
                @click="cancelSubscription"
              >
                {{ $t('Cancel Subscription') }}
              </b-button>
            </div>
          </b-col>
        </b-row>

        <div v-else-if="isBlocked">
          <CancellationResultCard
            :access-end-date="retainedAccessEndDate"
            :title="cancellationResultTitle"
            :message="retainedAccessMessage"
            :unavailable-message="cancellationDateUnavailableMessage"
          />
        </div>

        <div
          v-if="freeAccessActive || canDisplayOffer"
          class="subject-count"
        >
          <b-row
            class="pricing-card plan-options justify-content-center mt-2 mb-3"
            data-testid="available-plans"
          >
            <b-col
              v-if="freeAccessActive"
              cols="12"
              sm="10"
              :md="canDisplayOffer ? '12' : '8'"
              :lg="canDisplayOffer ? '6' : '5'"
              class="d-flex mb-2"
            >
              <SubscriptionSummaryCard
                v-bind="freeSummaryCardProps"
                class="price-card price-card--static w-100 mb-0 shadow-lg"
                body-class="plan-card-body"
              />
            </b-col>
            <b-col
              v-if="canDisplayOffer"
              cols="12"
              sm="10"
              :md="freeAccessActive ? '12' : '8'"
              :lg="freeAccessActive ? '6' : '5'"
              class="d-flex mb-2"
            >
              <b-card
                class="price-card text-left shadow-lg w-100 mb-0"
                body-class="plan-card-body d-flex flex-column"
                data-testid="subscription-offer-card"
                @click="proceedToPayment"
              >
                <div class="text-center mb-1">
                  <h3 class="font-weight-bolder text-primary mb-1">
                    {{ displayTerms.name }}
                  </h3>
                  <div class="d-flex flex-wrap justify-content-center align-items-end mt-2 mb-1">
                    <h1
                      class="font-weight-bolder mb-0"
                      style="font-size: 2.8rem; line-height: 1;"
                    >
                      {{ formatPrice(displayTerms.netPrice) }}
                    </h1>
                    <span class="text-muted font-weight-bold font-medium-1 ml-50 mb-1">+ %{{ displayTerms.vatRate * 100 }} {{ $t('KDV') }} / {{ $t('Ay') }}</span>
                  </div>
                  <div class="text-muted font-weight-bold mb-1">
                    {{ $t('Aylık toplam') }}: {{ formatPrice(displayTerms.grossPrice) }}
                  </div>
                  <div
                    v-if="offer.promotional"
                    class="mt-2"
                  >
                    <b-badge
                      variant="light-success"
                      pill
                      class="font-weight-bolder px-3 py-2 shadow-sm"
                      style="font-size: 1.15rem; border: 1px solid rgba(40, 199, 111, 0.5);"
                    >
                      <feather-icon
                        icon="GiftIcon"
                        size="18"
                        class="mr-50 mb-25"
                      />
                      {{ $t('İlk {duration} Ücretsiz', { duration: trialLabel }) }}
                    </b-badge>
                  </div>
                </div>

                <hr class="price-card__rule">

                <!-- Item 5: eight rows at mb-1 read as one solid block. The
                     list is now driven by the shared feature constant so the
                     subscription card renders exactly the same eight items. -->
                <ul class="list-unstyled price-card__features">
                  <li
                    v-for="feature in packageFeatures"
                    :key="feature"
                    class="price-card__feature d-flex align-items-center"
                  >
                    <feather-icon
                      icon="CheckCircleIcon"
                      class="text-primary mr-1 flex-shrink-0"
                      size="20"
                    />
                    <span class="font-weight-bold font-medium-1">{{ $t(feature) }}</span>
                  </li>
                </ul>

                <div class="mt-auto">
                  <b-alert
                    v-if="freeAccessActive"
                    show
                    variant="warning"
                    class="p-1 mb-2"
                    data-testid="early-purchase-notice"
                    @click.native.stop
                  >
                    {{ $t('freeAccess.earlyPurchaseNotice') }}
                  </b-alert>
                  <b-button
                    variant="primary"
                    class="anim-button w-100 font-weight-bold"
                    size="lg"
                    data-testid="start-subscription"
                    @click.stop="proceedToPayment"
                  >
                    {{ $t('Aboneliği Başlat') }}
                  </b-button>
                  <p class="text-center font-small-2 mt-1 mb-0">
                    <b-link
                      :to="{ name: 'legal-hub' }"
                      @click.stop
                    >
                      {{ $t('pricing.reviewInformationLink') }}
                    </b-link>
                  </p>
                </div>
              </b-card>
            </b-col>
          </b-row>
        </div>
      </template>
    </template>
  </section>
</template>

<script>
import {
  BRow, BCol, BCard, BCardText, BButton, BBadge, BCardTitle, BLink, BAlert,
} from 'bootstrap-vue'
import axios from '@axios'
import FreeAccessCelebration from '@/components/FreeAccessCelebration.vue'
import { freeAccessOf, isFreeAccessActive } from '@/libs/freeAccess'
import { freeAccessClaimErrorKey } from '@/libs/otpI18nKeys'
import CheckoutAddress from '@/views/pages/pricing/CheckoutAddress.vue'
import CancellationResultCard from '@/views/pages/pricing/CancellationResultCard.vue'
import SubscriptionSummaryCard from '@/views/pages/pricing/SubscriptionSummaryCard.vue'
import { SUBSCRIPTION_PACKAGE_FEATURES } from '@/libs/subscriptionPackageFeatures'
import {
  fetchSubscriptionStanding,
  invalidateSubscriptionStanding,
} from '@/libs/subscriptionStanding'
import {
  amountChargedToday,
  customerCommercialTerms,
  formatSubscriptionAccessEndDate,
  formatTry,
  normalizeCommercialOffer,
  trialDurationLabel,
} from '@/libs/subscriptionCommercial'

const {
  axiosRoutes: { subscription: subscriptionPath, user: userPath },
} = require('../../../../G_CONFIG')

const REVIEW_STATES = ['not-started', 'active', 'completed', 'not-applicable']

const normalizeCommercialStatus = standing => {
  const offer = normalizeCommercialOffer(standing && standing.offer)
  if (!standing
    || standing.contractComplete !== true
    || typeof standing.subscriptionStatus !== 'string'
    || typeof standing.hasSubscription !== 'boolean'
    || !standing.approvalReview
    || !REVIEW_STATES.includes(standing.approvalReview.state)
    || !standing.promotion
    || typeof standing.promotion.redeemed !== 'boolean'
    || !offer) return null
  return { standing, offer }
}

export default {
  components: {
    FreeAccessCelebration,
    BRow,
    BCol,
    BCard,
    BBadge,
    BCardText,
    BButton,
    BCardTitle,
    BLink,
    BAlert,
    CheckoutAddress,
    CancellationResultCard,
    SubscriptionSummaryCard,
  },
  data() {
    return {
      claiming: false,
      claimError: null,
      celebrationVisible: false,
      loadState: 'loading',
      planSelected: false,
      selectedPlanKey: null,
      selectedOffer: null,
      selectedReviewStartsOnSubscription: false,
      priceNcount: {
        totalPrice: 0,
        amount: 1,
        subTotal: 0,
        price: 0,
        paidPrice: 0,
        tax: 0,
        totaldiscountRate: 0,
        selectedPlan: { title: 'Monthly Subscription', amount: 1 },
      },
      subscriptionStatus: 'INACTIVE',
      hasSubscription: false,
      hasAccess: false,
      trialEndsAt: null,
      standing: null,
      account: null,
      offer: null,
      cancellationFlowInFlight: false,
      cancellationAccessEndDate: null,
    }
  },
  computed: {
    freeAccess() { return freeAccessOf(this.standing) },
    accountUserId() { return (this.account && (Reflect.get(this.account, '_id') || this.account.id)) || '' },
    freeAccessClaimable() {
      return Boolean(this.standing && this.standing.approvalReason === 'approved'
        && this.freeAccess.readinessKnown && this.freeAccess.claimable && !this.hasCurrentSubscription)
    },
    pendingAwaitingFreeAccess() {
      return Boolean(this.standing && this.standing.approvalReason === 'pending'
        && this.freeAccess.eligible && this.freeAccess.state === 'none' && !this.hasCurrentSubscription)
    },
    freeAccessActive() {
      return Boolean(this.standing && this.standing.approvalReason === 'approved'
        && this.standing.canUseGatedFeatures && isFreeAccessActive(this.freeAccess) && !this.hasCurrentSubscription)
    },
    freeAccessEnded() { return Boolean(this.standing && this.standing.approvalReason === 'approved' && this.standing.accessReason === 'free-access-ended') },
    freeSummaryCardProps() {
      return {
        planName: this.$t(['approval', 'self-claim'].includes(this.freeAccess.source)
          ? 'freeAccess.planName' : 'freeAccess.statusLabel'),
        statusLabel: this.$t('freeAccess.currentPlan'),
        statusFirst: true,
        statusVariant: 'light-info',
        statusIcon: 'GiftIcon',
        features: this.packageFeatures,
        accessDateLabel: this.$t('subscription.accessUntil', { date: this.formatDate(this.freeAccess.endsAt) }),
        remainingLabel: this.freeAccess.daysLeft === null
          ? '' : this.$t('freeAccess.daysLeft', { count: this.freeAccess.daysLeft }),
      }
    },
    displayTerms() {
      // L9: prefer the price the server resolved for the offer it will
      // actually let this account buy; SUBSCRIPTION_DISPLAY_TERMS is only the
      // fallback for a server that predates offer.pricing.
      return customerCommercialTerms(this.offer)
    },
    hasCurrentSubscription() {
      return ['TRIAL', 'ACTIVE', 'PAST_DUE'].includes(this.subscriptionStatus)
    },
    packageFeatures() {
      return SUBSCRIPTION_PACKAGE_FEATURES
    },
    // Item 7: a TRIAL subscriber is shown "Abonelik Aktif" as the PM asked --
    // the raw enum used to be printed straight to the customer. PAST_DUE is
    // deliberately NOT folded in: that account's charge failed and it is
    // inside the 5-day grace window, so calling it active would be false and
    // would hide the fact that the card needs fixing.
    subscriptionStatusLabel() {
      return this.subscriptionStatus === 'PAST_DUE'
        ? this.$t('subscription.status.pastDue')
        : this.$t('subscription.status.active')
    },
    subscriptionStatusVariant() {
      return this.subscriptionStatus === 'PAST_DUE' ? 'light-warning' : 'light-success'
    },
    subscriptionStatusIcon() {
      return this.subscriptionStatus === 'PAST_DUE' ? 'AlertCircleIcon' : 'CheckCircleIcon'
    },
    // Supporting detail rather than the headline. The status endpoint exposes
    // no renewal date, so for anything past the free period the line is simply
    // omitted rather than guessed at.
    //
    // Item 6 (PM 05.08.2026): the label now names the FREE-usage end date, so
    // it may only render while the free period is the thing that is running.
    // `trialEndsAt` is NOT cleared when a trial converts to paid
    // (server/services/pendingSubscriptionRecovery.js), so without the status
    // check a paying subscriber would be shown a free-usage end date that has
    // already passed.
    subscriptionAccessDateLabel() {
      if (!this.trialEndsAt || this.subscriptionStatus !== 'TRIAL') return ''
      return this.$t('subscription.accessUntil', { date: this.formatDate(this.trialEndsAt) })
    },
    // Three branches render the same card; bind them from one place so they
    // cannot drift apart.
    summaryCardProps() {
      return {
        planName: this.displayTerms.name,
        statusLabel: this.subscriptionStatusLabel,
        statusVariant: this.subscriptionStatusVariant,
        statusIcon: this.subscriptionStatusIcon,
        accessDateLabel: this.subscriptionAccessDateLabel,
        features: this.packageFeatures,
      }
    },
    isBlocked() {
      return Boolean(this.offer && this.offer.blocked)
    },
    accountBlocked() {
      return Boolean(this.standing && this.standing.accountBlocked)
    },
    approvalStatus() {
      return (this.standing && this.standing.approvalStatus) || null
    },
    approvalAllowed() {
      return Boolean(this.standing && this.standing.approvalAllowed)
    },
    applicationNotSubmitted() {
      return this.approvalStatus === 'not-submitted'
        || (this.standing && this.standing.approvalReason === 'approval-missing')
    },
    applicationRejected() {
      return this.approvalStatus === 'rejected' && !this.approvalAllowed
    },
    reviewState() {
      return this.standing && this.standing.approvalReview
        ? this.standing.approvalReview.state
        : null
    },
    reviewDeadlineAt() {
      return this.standing && this.standing.approvalReview
        ? this.standing.approvalReview.deadlineAt
        : null
    },
    reviewCompleted() {
      return this.reviewState === 'completed'
        && this.approvalStatus !== 'approved'
        && !this.applicationRejected
    },
    pendingPaidOfferUnavailable() {
      return this.standing
        && this.standing.approvalReason === 'pending'
        && !this.pendingAwaitingFreeAccess
        && !this.hasCurrentSubscription
        && Boolean(this.offer && this.offer.planKey)
        && Number(this.offer.trialPeriodDays) <= 0
    },
    reviewStartsOnSubscription() {
      return this.standing
        && this.standing.approvalReason === 'pending'
        && this.reviewState === 'not-started'
        && this.hasCurrentSubscription
    },
    promotionRedeemed() {
      return Boolean(this.standing && this.standing.promotion && this.standing.promotion.redeemed)
    },
    reviewComment() {
      if (!this.account) return null
      return (this.account.approval && this.account.approval.reviewComment)
        || this.account.reviewComment
        || null
    },
    canDisplayOffer() {
      return this.loadState === 'loaded'
        && Boolean(this.offer && !this.offer.blocked && this.offer.planKey)
        && !this.hasCurrentSubscription
        && !this.pendingPaidOfferUnavailable
        && !this.freeAccessClaimable && !this.pendingAwaitingFreeAccess && !this.applicationNotSubmitted
        && (this.approvalAllowed || this.applicationNotSubmitted)
    },
    canStartCheckout() {
      return this.canDisplayOffer
        && this.approvalAllowed
        && !this.applicationNotSubmitted
    },
    retainedAccessEndDate() {
      return this.cancellationAccessEndDate
        || formatSubscriptionAccessEndDate(this.offer && this.offer.blockedUntil)
    },
    retainedAccessMessage() {
      // The persistent post-reload card has no signal for which cohort
      // (trial vs paid) was canceled, so unlike cancelSubscription()'s dialog
      // copy this stays status-neutral — accurate for either one instead of
      // guessing trial framing.
      return this.retainedAccessEndDate
        ? this.$t('pricing.cancellation.retainedAccessResult', { date: this.retainedAccessEndDate })
        : ''
    },
    cancellationResultTitle() {
      return this.$t('pricing.cancellation.resultTitle')
    },
    cancellationDateUnavailableMessage() {
      return this.$t('pricing.cancellation.dateUnavailable')
    },
    trialLabel() {
      return trialDurationLabel(this.offer && this.offer.trialPeriodDays)
    },
  },
  created() {
    this.loadSubscriptionStatus()
  },
  methods: {
    async claimFreeAccess() {
      if (this.claiming || !this.freeAccessClaimable) return
      this.claiming = true
      this.claimError = null
      try {
        await axios.post(subscriptionPath.freeAccess, null, { suppressErrorToast: true })
        invalidateSubscriptionStanding()
        await this.loadSubscriptionStatus()
        this.celebrationVisible = this.freeAccessActive
      } catch (error) {
        this.claimError = freeAccessClaimErrorKey(error)
        await this.loadSubscriptionStatus()
      } finally { this.claiming = false }
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString('tr-TR', {
        timeZone: 'Europe/Istanbul',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    },
    formatPrice(value) {
      return formatTry(value, this.displayTerms.currency)
    },
    async loadSubscriptionStatus() {
      this.loadState = 'loading'
      this.offer = null
      try {
        const snapshot = await fetchSubscriptionStanding({ forceRefresh: true })
        const commercialStatus = normalizeCommercialStatus(snapshot)
        if (!commercialStatus) {
          throw new Error('Incomplete subscription status')
        }
        this.standing = commercialStatus.standing
        this.offer = commercialStatus.offer
        this.subscriptionStatus = this.standing.subscriptionStatus
        this.hasSubscription = this.standing.hasSubscription
        this.hasAccess = this.standing.hasAccess === true
        this.trialEndsAt = this.standing.trialEndsAt || null
        this.loadState = 'loaded'

        // The status endpoint is commercial authority. /user/my is fetched
        // only for the applicant-visible review note and never for dates,
        // access, offer selection, or promotion eligibility.
        axios.get(userPath.my)
          .then(accountRes => { this.account = accountRes && accountRes.data })
          .catch(() => {})
      } catch (error) {
        this.standing = null
        this.offer = null
        this.loadState = 'error'
      }
    },
    async proceedToPayment() {
      if (!this.canDisplayOffer) return
      if (!this.canStartCheckout) {
        if (!this.applicationNotSubmitted) return
        await this.$swal({
          text: this.$t('pricing.accountCompletionRequired'),
          icon: 'warning',
          confirmButtonText: this.$t('Ok'),
          customClass: { confirmButton: 'btn btn-primary' },
          buttonsStyling: false,
        })
        this.$router.push({ name: 'account-settings' }).catch(() => {})
        return
      }
      this.selectedOffer = Object.freeze({ ...this.offer })
      this.selectedPlanKey = this.selectedOffer.planKey
      this.selectedReviewStartsOnSubscription = this.reviewStartsOnSubscription
      const today = amountChargedToday(this.selectedOffer)
      const terms = customerCommercialTerms(this.selectedOffer)
      this.priceNcount.selectedPlan = {
        title: terms.name,
        amount: 1,
      }
      this.priceNcount.price = terms.grossPrice
      this.priceNcount.paidPrice = today
      this.priceNcount.totalPrice = today
      this.planSelected = true
    },
    handleOfferChanged() {
      this.planSelected = false
      this.selectedPlanKey = null
      this.selectedOffer = null
      this.selectedReviewStartsOnSubscription = false
      this.loadSubscriptionStatus()
    },
    async cancelSubscription() {
      if (this.cancellationFlowInFlight) return
      this.cancellationFlowInFlight = true
      // The status at the moment Cancel is pressed decides the copy: a TRIAL
      // cancellation forfeits the free entitlement, while ACTIVE/PAST_DUE
      // just stop auto-renewal at the paid period's end. subscriptionStatus
      // is only overwritten later by loadSubscriptionStatus() below.
      const isTrialCancellation = this.subscriptionStatus === 'TRIAL'
      const resultMessageKey = isTrialCancellation
        ? 'pricing.cancellation.trialResult'
        : 'pricing.cancellation.paidResult'
      try {
        const result = await this.$swal({
          title: this.$t('pricing.cancellation.confirmTitle'),
          html: this.$t(isTrialCancellation ? 'pricing.cancellation.trialConfirmHtml' : 'pricing.cancellation.paidConfirmHtml'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: this.$t('pricing.cancellation.confirmYes'),
          cancelButtonText: this.$t('pricing.cancellation.confirmNo'),
          focusCancel: true,
          // Item 9: the emphasis was inverted -- the destructive action got
          // the filled brand button while the safe one was a text link. Only
          // the STYLING swaps here; confirmButtonText stays bound to the
          // confirm action and cancelButtonText to cancel, so each button
          // still does exactly what its label says.
          customClass: {
            confirmButton: 'btn btn-outline-secondary',
            cancelButton: 'btn btn-primary mr-1',
          },
          reverseButtons: true,
          buttonsStyling: false,
        })
        if (!(result && (result.value || result.isConfirmed))) return

        let response
        try {
          response = await axios.post(subscriptionPath.cancel)
        } catch (err) {
          await this.$swal({
            title: this.$t('Could not cancel subscription'),
            text: (err && err.response && err.response.data && (err.response.data.error || err.response.data.message)) || this.$t('Could not reach the server. Please try again.'),
            icon: 'error',
            confirmButtonText: this.$t('Ok'),
            customClass: { confirmButton: 'btn btn-primary' },
            buttonsStyling: false,
          })
          return
        }

        const responseAccessEndDate = formatSubscriptionAccessEndDate(
          response && response.data && response.data.subscriptionAccessEndsAt,
        )
        invalidateSubscriptionStanding()
        await this.loadSubscriptionStatus()
        const refreshedAccessEndDate = formatSubscriptionAccessEndDate(
          this.offer && this.offer.blockedUntil,
        )
        this.cancellationAccessEndDate = responseAccessEndDate || refreshedAccessEndDate

        if (!this.cancellationAccessEndDate) {
          await this.$swal({
            title: this.$t('pricing.cancellation.resultTitle'),
            text: this.$t('pricing.cancellation.dateUnavailable'),
            icon: 'warning',
            confirmButtonText: this.$t('Ok'),
            customClass: { confirmButton: 'btn btn-primary' },
            buttonsStyling: false,
          })
          return
        }

        await this.$swal({
          title: this.$t('pricing.cancellation.resultTitle'),
          text: this.$t(resultMessageKey, { date: this.cancellationAccessEndDate }),
          icon: 'success',
          confirmButtonText: this.$t('Ok'),
          customClass: { confirmButton: 'btn btn-primary' },
          buttonsStyling: false,
        })
      } finally {
        this.cancellationFlowInFlight = false
      }
    },
  },
}
</script>

<style lang="scss">
.anim-button {
  animation: mymove 2s infinite;
}
@keyframes mymove {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}
.price-card {
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  border: 2px solid #de39a5;
  border-radius: 1rem;
}
.price-card:hover {
  transform: scale(1.03);
}
.price-card--static {
  cursor: default;
}
.price-card--static:hover {
  transform: none;
}
.plan-options {
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}
.plan-card-body {
  padding: 2rem;
}
@media (max-width: 575.98px) {
  .plan-card-body {
    padding: 1.5rem;
  }
}
/* Item 5: the card packed a price block, a hairline rule and eight feature
   rows at mb-1 into one mass. These open up the three zones without pushing
   the CTA off a phone screen. */
.price-card__rule {
  width: 100%;
  margin-top: 1.75rem;
  margin-bottom: 1.75rem;
}
.price-card__features {
  margin-bottom: 2rem;
}
.price-card__feature + .price-card__feature {
  margin-top: 1rem;
}
@import "@core/scss/vue/pages/page-pricing.scss";
</style>
